import { useMemo } from "react";

import { useCompositedRecordingStream } from "./useCompositedRecordingStream";
import type {
  VideoClipPipPosition,
  VideoClipPipShape,
  VideoClipPipSize,
} from "./useVideoClipSettingsStore";

interface UseVideoClipStreamsOptions {
  cameraStream: MediaStream | null;
  isMicEnabled: boolean;
  isScreenSharing: boolean;
  pipPosition: VideoClipPipPosition;
  pipShape: VideoClipPipShape;
  pipSize: VideoClipPipSize;
  screenStream: MediaStream | null;
}

interface UseVideoClipStreamsResult {
  previewStream: MediaStream | null;
  recordingStream: MediaStream | null;
}

export const useVideoClipStreams = ({
  cameraStream,
  isMicEnabled,
  isScreenSharing,
  pipPosition,
  pipShape,
  pipSize,
  screenStream,
}: UseVideoClipStreamsOptions): UseVideoClipStreamsResult => {
  const compositedRecordingStream = useCompositedRecordingStream({
    cameraStream,
    isMicEnabled,
    pipPosition,
    pipShape,
    pipSize,
    screenStream,
  });

  const recordingStream = useMemo(() => {
    if (compositedRecordingStream) return compositedRecordingStream;
    if (isScreenSharing && screenStream) {
      const audioTracks = isMicEnabled
        ? (cameraStream?.getAudioTracks() ?? [])
        : [];
      return new MediaStream([
        ...screenStream.getVideoTracks(),
        ...audioTracks,
      ]);
    }
    return cameraStream;
  }, [
    compositedRecordingStream,
    isMicEnabled,
    isScreenSharing,
    screenStream,
    cameraStream,
  ]);

  const previewStream = useMemo(() => {
    if (isScreenSharing && screenStream) return screenStream;
    return cameraStream;
  }, [cameraStream, isScreenSharing, screenStream]);

  return { previewStream, recordingStream };
};
